import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import useAuthStore from '@/store/authStore';
import {
   getMikrotikHybridStatus,
   switchMikrotikToHybrid,
   moveMikrotikBackToCloud,
   type MikrotikHybridStatus,
} from '@/api/mikrotikHybrid';

/**
 * Cloud ⇄ Hybrid for a MikroTik vendo. Sits at the top of Device Settings,
 * Updates tab, where the Ruijie switch sits.
 *
 * Unlike the Ruijie switch this one is NOT finished when the button returns.
 * The button only prepares the router and tells the NodeMCU which firmware to
 * fetch; the vendo actually changes when the NodeMCU reports in on it, a
 * minute or two later. So this shows the steps and follows them, instead of a
 * toast that claims it is done.
 */

interface Props {
   deviceId: string;
   routerStatus?: 'connected' | 'agent-down' | 'disconnected' | 'unprovisioned';
}

const isMoving = (s?: MikrotikHybridStatus | null) =>
   !!s?.move && (s.move.phase === 'awaiting-firmware' || s.move.phase === 'cutting-over');

/** "2 min ago" — a clock time means nothing to someone on another screen. */
export function ago(iso: string | null | undefined, now: number = Date.now()): string {
   if (!iso) return '';
   const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
   if (s < 60) return 'just now';
   const m = Math.round(s / 60);
   if (m < 60) return `${m} min ago`;
   const h = Math.round(m / 60);
   return h < 24 ? `${h} hr ago` : `${Math.round(h / 24)} days ago`;
}

const MikrotikHybridSwitch: React.FC<Props> = ({ deviceId, routerStatus }) => {
   const queryClient = useQueryClient();
   const [busy, setBusy] = useState(false);
   const [confirming, setConfirming] = useState<'to-hybrid' | 'to-cloud' | 'cancel' | null>(null);

   // Moving BACK to Cloud is SmartFi admin only (Ray, 2026-10-08); the
   // server refuses everyone else, so the button is not shown to them.
   const isAdmin = useAuthStore((st) => st.user?.role === 'admin');
   const { data: status } = useQuery({
      queryKey: ['mikrotik-hybrid-status', deviceId],
      queryFn: () => getMikrotikHybridStatus(deviceId),
      // Follow a move closely; otherwise this is a slow-changing fact.
      refetchInterval: (q) => (isMoving(q.state.data as MikrotikHybridStatus | undefined) ? 4000 : 30000),
   });

   if (!status) return null;
   const moving = isMoving(status);
   // A Cloud vendo whose owner may not switch yet: the button is shown greyed
   // with the reason under it, so the operator knows it is coming (Ray,
   // 2026-10-12: "tell them this will be available once the hybrid is
   // proven"). New vendos can already be registered as Hybrid.
   const betaLocked = !status.canSwitch && !moving && status.mode === 'cloud';
   // A hybrid vendo with nothing to press and nothing happening = no box.
   if (!status.canSwitch && !moving && !betaLocked) return null;

   const refresh = async () => {
      await queryClient.invalidateQueries({ queryKey: ['mikrotik-hybrid-status', deviceId] });
      await queryClient.invalidateQueries({ queryKey: ['device-update-status', deviceId] });
   };

   const run = async (fn: () => Promise<unknown>, ok: string) => {
      setBusy(true);
      try {
         await fn();
         toast.success(ok);
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'That did not go through. Try again.');
      } finally {
         await refresh();
         setBusy(false);
      }
   };

   const routerDown =
      routerStatus === 'connected' || routerStatus === undefined
         ? null
         : 'The router is offline. Turn it on and try again once it shows online.';
   const blocked = status.blocked || routerDown;

   const move = status.move;
   const toHybrid = move?.direction === 'to-hybrid';
   const fw = move?.targetFirmwareVersion || '';
   const cutting = move?.phase === 'cutting-over';
   const justDone =
      move?.phase === 'done' &&
      !!move.finishedAt &&
      Date.now() - new Date(move.finishedAt).getTime() < 15 * 60 * 1000;

   return (
      <Box data-testid="mikrotik-hybrid-switch">
         <Head>
            MikroTik Hybrid <Beta>Beta</Beta>
         </Head>

         {moving && move ? (
            <>
               <Line>
                  Switching <Was>{toHybrid ? 'Cloud' : 'Hybrid'}</Was>
                  {' → '}
                  <Will>{toHybrid ? 'Hybrid' : 'Cloud'}</Will>
                  {move.startedAt && <Started> · started {ago(move.startedAt)}</Started>}
               </Line>
               <Steps>
                  <Step $state="done">
                     <Dot $state="done">✓</Dot>
                     {toHybrid ? 'Router is ready' : 'NodeMCU told to go back'}
                  </Step>
                  <Step $state={cutting ? 'done' : 'now'}>
                     <Dot $state={cutting ? 'done' : 'now'}>{cutting ? '✓' : <Spin />}</Dot>
                     <span>
                        NodeMCU is installing firmware {fw}
                        {!cutting && (
                           <Small>
                              Usually 1 to 2 minutes. The coin slot is off until it is back.
                              {!status.coinBoxOnline &&
                                 ' The NodeMCU is not answering right now. It continues by itself when it is back.'}
                           </Small>
                        )}
                     </span>
                  </Step>
                  <Step $state={cutting ? 'now' : 'todo'}>
                     <Dot $state={cutting ? 'now' : 'todo'}>{cutting ? <Spin /> : '3'}</Dot>
                     Moving customers over. They keep their remaining time.
                  </Step>
               </Steps>
               {move.error && (
                  <Note>
                     Last try did not finish ({move.error}). It tries again by itself.
                  </Note>
               )}
               {/* A move to Hybrid that is still waiting for the NodeMCU can be
                   called off. Going back to Cloud cannot: it is already the
                   way out. */}
               {status.canSwitch && toHybrid && !cutting && (
                  confirming === 'cancel' ? (
                     <Confirm>
                        <strong>Stop the switch and stay on Cloud?</strong>
                        <Row>
                           <Ghost type="button" disabled={busy} onClick={() => setConfirming(null)}>
                              No, keep going
                           </Ghost>
                           <Primary
                              type="button"
                              $danger
                              disabled={busy}
                              onClick={() => {
                                 setConfirming(null);
                                 run(() => moveMikrotikBackToCloud(deviceId), 'Stopped. This vendo stays on Cloud.');
                              }}
                           >
                              {busy ? 'Working…' : 'Yes, stay on Cloud'}
                           </Primary>
                        </Row>
                     </Confirm>
                  ) : (
                     <Row>
                        <Ghost type="button" disabled={busy} onClick={() => setConfirming('cancel')}>
                           Cancel the switch
                        </Ghost>
                     </Row>
                  )
               )}
            </>
         ) : (
            <>
               <Line>
                  This vendo runs on <Will>{status.mode === 'hybrid' ? 'Hybrid' : 'Cloud'}</Will>
                  {justDone && move?.finishedAt && <Done> ✓ switched {ago(move.finishedAt)}</Done>}
               </Line>

               {move?.phase === 'failed' && (
                  <Failed>
                     The switch to {toHybrid ? 'Hybrid' : 'Cloud'} did not finish
                     {move.error ? `: ${move.error}` : '.'}
                  </Failed>
               )}

               {confirming === 'to-hybrid' || confirming === 'to-cloud' ? (
                  <Confirm>
                     <strong>
                        {confirming === 'to-hybrid'
                           ? 'Switch this vendo to Hybrid?'
                           : 'Move this vendo back to Cloud?'}
                     </strong>
                     <ul>
                        <li>
                           The NodeMCU installs {confirming === 'to-hybrid' ? 'the Hybrid' : 'the Cloud'} firmware.
                           The coin slot is off for about 2 minutes, then comes back by itself.
                        </li>
                        <li>Customers who are online stay online and keep their remaining time.</li>
                        <li>
                           {confirming === 'to-hybrid'
                              ? 'After the switch, coins and the portal keep working even when the internet is down.'
                              : 'After the switch, coins need the internet again.'}
                        </li>
                     </ul>
                     <Row>
                        <Ghost type="button" disabled={busy} onClick={() => setConfirming(null)}>
                           Cancel
                        </Ghost>
                        <Primary
                           type="button"
                           $danger={confirming === 'to-cloud'}
                           disabled={busy}
                           onClick={() => {
                              const dir = confirming;
                              setConfirming(null);
                              run(
                                 () =>
                                    dir === 'to-hybrid'
                                       ? switchMikrotikToHybrid(deviceId)
                                       : moveMikrotikBackToCloud(deviceId),
                                 dir === 'to-hybrid'
                                    ? 'Router is ready. Waiting for the NodeMCU to install its firmware.'
                                    : 'Waiting for the NodeMCU to go back to the Cloud firmware.'
                              );
                           }}
                        >
                           {busy
                              ? 'Working…'
                              : confirming === 'to-hybrid'
                              ? 'Yes, switch to Hybrid'
                              : 'Yes, back to Cloud'}
                        </Primary>
                     </Row>
                  </Confirm>
               ) : (
                  <Row>
                     {status.mode === 'cloud' && (
                        <Primary
                           type="button"
                           disabled={busy || !!blocked || betaLocked}
                           title={betaLocked ? 'Available once Hybrid is proven.' : blocked || undefined}
                           onClick={() => !betaLocked && setConfirming('to-hybrid')}
                        >
                           {busy ? 'Preparing the router…' : 'Switch to Hybrid'}
                        </Primary>
                     )}
                     {/* Also offered after a failed move to Hybrid, whose way
                         out is the same request. */}
                     {isAdmin && (status.mode === 'hybrid' || (move?.phase === 'failed' && toHybrid)) && (
                        <Ghost
                           type="button"
                           disabled={busy || !!(status.mode === 'hybrid' ? blocked : routerDown)}
                           title={(status.mode === 'hybrid' ? blocked : routerDown) || undefined}
                           onClick={() => setConfirming('to-cloud')}
                        >
                           Move back to Cloud
                        </Ghost>
                     )}
                  </Row>
               )}
               {busy && <Note>Preparing the router. This takes up to a minute. Keep this window open.</Note>}
               {betaLocked && (
                  <Note data-testid="hybrid-switch-beta-note">
                     Switching a running vendo to Hybrid opens once Hybrid is proven on real vendos. A
                     new vendo can already be registered as Hybrid.
                  </Note>
               )}
               {!busy && blocked && <Note>{blocked}</Note>}
            </>
         )}
      </Box>
   );
};

export default MikrotikHybridSwitch;

const Box = styled.div`
   margin-bottom: 16px;
   padding: 14px 16px;
   border: 1px solid ${colors.PRIMARY}33;
   border-radius: 12px;
   background: ${colors.PRIMARY}0a;
`;

const Head = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 11.5px;
   font-weight: 800;
   letter-spacing: 0.4px;
   text-transform: uppercase;
   color: ${colors.GRAY5};
   margin-bottom: 6px;
`;

const Beta = styled.span`
   padding: 2px 8px;
   border-radius: 999px;
   background: ${colors.ORANGE};
   color: #fff;
   font-size: 10px;
   font-weight: 800;
   letter-spacing: 0.6px;
   line-height: 1.5;
`;

const Line = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const Was = styled.span`
   font-weight: 800;
   color: ${colors.GRAY1};
`;

const Will = styled.span`
   font-weight: 800;
   color: ${colors.PRIMARY};
`;

const Started = styled.span`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY5};
`;

const Done = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.SUCCESS};
`;

const Steps = styled.div`
   margin-top: 12px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

type StepState = 'done' | 'now' | 'todo';

const Step = styled.div<{ $state: StepState }>`
   display: flex;
   align-items: flex-start;
   gap: 10px;
   font-size: 13px;
   font-weight: ${({ $state }) => ($state === 'now' ? 700 : 600)};
   color: ${({ $state }) => ($state === 'todo' ? colors.GRAY5 : colors.MAIN_DARK)};
`;

const Dot = styled.span<{ $state: StepState }>`
   flex: 0 0 20px;
   height: 20px;
   border-radius: 50%;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 11px;
   font-weight: 800;
   color: ${({ $state }) => ($state === 'todo' ? colors.GRAY5 : '#fff')};
   background: ${({ $state }) =>
      $state === 'done' ? colors.SUCCESS : $state === 'now' ? colors.PRIMARY : 'transparent'};
   border: 1px solid
      ${({ $state }) =>
         $state === 'done' ? colors.SUCCESS : $state === 'now' ? colors.PRIMARY : colors.GRAY10};
`;

const turn = keyframes`
   to { transform: rotate(360deg); }
`;

const Spin = styled.span`
   width: 10px;
   height: 10px;
   border-radius: 50%;
   border: 2px solid #ffffff66;
   border-top-color: #fff;
   animation: ${turn} 0.8s linear infinite;
`;

const Small = styled.span`
   display: block;
   margin-top: 2px;
   font-size: 11.5px;
   font-weight: 500;
   color: ${colors.GRAY5};
`;

const Confirm = styled.div`
   margin-top: 12px;
   padding: 12px 14px;
   border-radius: 10px;
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}0d;
   font-size: 12.5px;
   line-height: 1.5;
   color: ${colors.GRAY1};
   strong {
      color: ${colors.MAIN_DARK};
   }
   ul {
      margin: 8px 0 0;
      padding-left: 18px;
   }
   li {
      margin-bottom: 4px;
   }
`;

const Row = styled.div`
   display: flex;
   gap: 8px;
   margin-top: 12px;
   flex-wrap: wrap;
`;

const Primary = styled.button<{ $danger?: boolean }>`
   border: 0;
   border-radius: 8px;
   padding: 9px 14px;
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.WHITE};
   background: ${({ $danger }) => ($danger ? colors.RED : colors.PRIMARY)};
   cursor: pointer;
   &:disabled {
      background: ${colors.GRAY10};
      color: ${colors.GRAY1};
      cursor: not-allowed;
   }
`;

const Ghost = styled.button`
   border: 1px solid ${colors.GRAY10};
   border-radius: 8px;
   padding: 9px 14px;
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.GRAY1};
   background: transparent;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const Note = styled.div`
   margin-top: 8px;
   font-size: 11.5px;
   color: ${colors.GRAY5};
`;

const Failed = styled.div`
   margin-top: 8px;
   font-size: 12.5px;
   font-weight: 600;
   color: ${colors.DANGER};
`;
